// ===============================================================
// Forms — Dashboard
// Key metrics, recent responses overview
// ===============================================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, SimpleGrid, Group, Stack, Badge, Button,
  Loader, Center,
} from '../../components/gab/GabComponents';
import {
  IconTemplate, IconFileCheck, IconClipboardList, IconWorld,
} from '@tabler/icons-react';
import { useFormsStore } from '../../store/formsStore';
import { semantic } from '../../lib/colors';

export default function FormsDashboard() {
  const navigate = useNavigate();
  const {
    templates, responses, loading,
    loadTemplates, loadResponses,
  } = useFormsStore();

  useEffect(() => {
    loadTemplates();
    loadResponses();
  }, []);

  if (loading) {
    return <Center p="xl"><Loader /></Center>;
  }

  const totalTemplates = templates.length;
  const activeTemplates = templates.filter(t => t.status === 'published');
  const totalResponses = responses.length;
  const publicForms = templates.filter(t => (t as any).isPublic === 'true' || (t as any).isPublic === true);

  // Recent responses (last 10, enriched with template name)
  const recentResponses = [...responses]
    .map(r => {
      const tpl = templates.find(t => String(t.id) === String(r.templateId) || String(t.Rid) === String(r.templateId));
      return { ...r, templateName: r.templateName || (tpl as any)?.templateName || (tpl as any)?.title || 'Untitled Form' };
    })
    .sort((a, b) => {
      const da = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      const db = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return db - da;
    })
    .slice(0, 10);

  const statusColor = (s: string) => {
    switch (s) {
      case 'reviewed': return 'blue';
      case 'complete': return 'green';
      case 'new': return 'gray';
      default: return 'gray';
    }
  };

  return (
    <div>
      <Title order={3} mb="md" c="black">Forms Dashboard</Title>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }}
          onClick={() => navigate('/forms/templates')}>
          <IconTemplate size={20} color="var(--blurple)" style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700}>{totalTemplates}</Text>
          <Text size="xs" c="dimmed">Total Templates</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }}
          onClick={() => navigate('/forms/templates?status=published')}>
          <IconFileCheck size={20} color="#07963F" style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c="#07963F">{activeTemplates.length}</Text>
          <Text size="xs" c="dimmed">Active Forms</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }}
          onClick={() => navigate('/forms/responses')}>
          <IconClipboardList size={20} color={semantic.warning} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.warning}>{totalResponses}</Text>
          <Text size="xs" c="dimmed">Total Responses</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }}
          onClick={() => navigate('/forms/templates?status=published&public=true')}>
          <IconWorld size={20} color={semantic.info} style={{ marginBottom: 4 }} />
          <Text fz={28} fw={700} c={semantic.info}>{publicForms.length}</Text>
          <Text size="xs" c="dimmed">Public Forms</Text>
        </Paper>
      </SimpleGrid>

      {/* Recent Responses */}
      <Paper p="md" radius="sm" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={600} size="sm">Recent Responses</Text>
          <Button variant="subtle" size="xs" onClick={() => navigate('/forms/responses')}>View All</Button>
        </Group>
        {recentResponses.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">No responses yet</Text>
        ) : (
          <Stack gap="xs" style={{ maxHeight: 450, overflowY: 'auto' }}>
            {recentResponses.map(r => (
              <Paper key={r.id} p="sm" radius="sm" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>{r.templateName || 'Untitled Form'}</Text>
                    <Text size="xs" c="dimmed">
                      {r.respondentName || 'Anonymous'}
                      {r.incidentId ? ` — Incident #${r.incidentId}` : ''}
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Badge size="xs" color={statusColor(r.status)}>{r.status}</Badge>
                    {r.submittedAt && (
                      <Text size="xs" c="dimmed">
                        {new Date(r.submittedAt).toLocaleDateString()}{' '}
                        {new Date(r.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Text>
                    )}
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </div>
  );
}
